import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import {
    ArrowLeft,
    ArrowRight,
    Columns,
    LinkIcon,
    Minus,
    Plus,
    Redo,
    Rows,
    Table as TableIcon,
    Trash2,
    Undo,
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { type TipTapDoc, emptyTipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { getCompassTipTapExtensions } from "@compass/shared/tiptap/extensions";
import { TipTapLinkDialog } from "./TipTapLink";

type EditorFeatures = {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
    heading?: boolean;
    blockquote?: boolean;
    bulletList?: boolean;
    orderedList?: boolean;
    /** Legacy field — indent is no longer a standalone feature. The Tab key
     *  still nests list items; this flag now controls only whether the
     *  indent buttons appear in the toolbar. */
    indent?: boolean;
    redoUndo?: boolean;
    link?: boolean;
    table?: boolean;
};

type PostEditorProps = {
    autoFocus?: boolean;
    className?: string;
    /** TipTap doc to seed the editor with. Pass `null`/`undefined` for empty. */
    initialValue?: TipTapDoc | null;
    onChange?: (doc: TipTapDoc) => void;
    onEnter?: (e?: React.FormEvent<HTMLFormElement>) => void;
    placeholder?: string;
    wrapperClassName?: string;
    features?: EditorFeatures;
    contentHeightClassName?: string;
    componentType?: string;
    toolbarBgColor?: string;
    /** Extra nodes rendered inline in the toolbar's left button group (e.g. the
     *  vendor composer's attachment icons), so they sit next to the formatting
     *  buttons instead of on a separate row. */
    toolbarExtras?: React.ReactNode;
};

export const PostEditor = forwardRef<Editor, PostEditorProps>((props, ref) => {
    const defaultFeatures: Required<EditorFeatures> = {
        bold: true,
        italic: true,
        underline: true,
        strike: true,
        heading: true,
        blockquote: true,
        bulletList: true,
        orderedList: true,
        indent: true,
        redoUndo: true,
        link: true,
        table: true,
    };

    const features = { ...defaultFeatures, ...props.features };

    const extensions = getCompassTipTapExtensions({
        bold: features.bold,
        italic: features.italic,
        underline: features.underline,
        strike: features.strike,
        heading: features.heading ? { levels: [1, 2, 3, 4, 5, 6] } : false,
        blockquote: features.blockquote,
        bulletList: features.bulletList,
        orderedList: features.orderedList,
        link: features.link,
        table: features.table,
        placeholder: props.placeholder || "Type here...",
    });

    const [isLinkDialogOpen, setLinkDialogOpen] = useState(false);
    const [url, setUrl] = useState("");
    const [title, setTitle] = useState("");

    const {
        autoFocus,
        initialValue,
        onChange,
        wrapperClassName,
        contentHeightClassName = "min-h-[120px]",
        componentType,
        toolbarBgColor,
    } = props;

    const editor = useEditor({
        extensions,
        content: initialValue ?? emptyTipTapDoc(),
        // The toolbar reads editor.isActive() / can() while rendering, so it has
        // to re-render on every transaction (TipTap 2's default; 3 turned it off).
        shouldRerenderOnTransaction: true,
        editorProps: {
            attributes: {
                class: "min-h-[40px] max-h-[120px] overflow-y-auto relative",
            },
            handleKeyDown(view, event) {
                // Submit only on cmd/ctrl+Enter. Plain Enter must always fall
                // through to ProseMirror so list items, blockquotes, and
                // paragraphs split correctly — the previous Enter-submits
                // behavior fought with TipTap's list keymap (PR #847 worked
                // around this by removing lists; this is the proper fix).
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    props.onEnter?.();
                    return true;
                }

                if (event.key === "Enter" && event.shiftKey) {
                    return false;
                }
                if (event.key === " " || event.code === "Space") {
                    const { state, dispatch } = view;
                    const { $from, from } = state.selection;

                    const insideLink = $from.marks().some((mark) => mark.type.name === "link");

                    if (insideLink) {
                        // break the link mark before inserting space
                        const tr = state.tr.removeMark(from, from, view.state.schema.marks.link);
                        dispatch(tr);
                    }
                }

                return false;
            },
        },
        autofocus: autoFocus ? "start" : false,
        onCreate: ({ editor }) => {
            // Fire an initial onChange so callers that store contentJson in
            // state (typed `TipTapDoc | null`) hold a non-null doc from the
            // moment the editor mounts. Without this, attachment-only or
            // poll-only submissions never populate parent state and the
            // submit guard `if (!contentJson) return` blocks valid sends.
            onChange?.(editor.getJSON() as unknown as TipTapDoc);
        },
        onUpdate: ({ editor }) => {
            onChange?.(editor.getJSON() as unknown as TipTapDoc);
        },
    });

    useImperativeHandle(ref, () => editor as Editor, [editor]);

    useEffect(() => {
        if (!editor) return;
        if (!initialValue) return;
        // Avoid an infinite re-seed loop: only push new content when the
        // incoming doc differs from what the editor already holds.
        const current = editor.getJSON();
        if (JSON.stringify(current) !== JSON.stringify(initialValue)) {
            editor.commands.setContent(initialValue, { emitUpdate: false });
        }
    }, [initialValue, editor]);

    if (!editor) return;

    const openLinkDialog = () => {
        const { from, to } = editor.state.selection;
        const selectedText = editor?.state.doc.textBetween(from, to, " ");
        const linkAttrs = editor.getAttributes("link");
        const isLink = !!linkAttrs?.href;

        if (isLink) {
            setUrl(linkAttrs.href || "");
            setTitle(linkAttrs.title || selectedText || "");
        } else {
            setUrl("");
            setTitle(selectedText || "");
        }
        setLinkDialogOpen(true);
    };

    const insertLink = (url: string, title: string) => {
        editor
            .chain()
            .focus()
            .insertContent([
                {
                    type: "text",
                    text: title || url,
                    marks: [
                        {
                            type: "link",
                            attrs: {
                                href: url,
                            },
                        },
                    ],
                },
                {
                    type: "text",
                    text: " ",
                },
            ])
            .run();
        setLinkDialogOpen(false);
    };

    const btn = (name: string, attrs?: any) =>
        `rounded px-2 py-1 ${editor.isActive(name, attrs) ? "bg-gray-300" : ""}`;

    const toggleHeading = (level: 1 | 2) => {
        const isCurrentLevel = editor.isActive("heading", { level });

        if (isCurrentLevel) {
            // If already this heading level, convert to paragraph
            editor.chain().focus().setParagraph().run();
        } else {
            // Otherwise, set to this heading level
            editor.chain().focus().toggleHeading({ level }).run();
        }
    };

    return (
        <div className={wrapperClassName}>
            {componentType === "message" && (
                <div className="mb-2 flex justify-end pt-2 text-sm text-[#424242]">
                    <div>
                        <span className="font-semibold">⌘ / Ctrl + Enter </span> to send
                    </div>
                </div>
            )}
            <div
                className={`${contentHeightClassName} ___mb-1 w-full rounded-lg bg-[#eeeeee] p-3 text-sm text-gray-800 outline-hidden placeholder:text-gray-400`}
            >
                <EditorContent
                    editor={editor}
                    className="prose prose-sm list-outside text-sm wrap-break-word whitespace-pre-wrap text-[#424242] [&_.selectedCell]:bg-blue-100 [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:mb-3 [&_blockquote]:ml-0 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_h1]:mt-6 [&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-5 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-bold [&_li]:ml-6 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol_ol]:ml-10 [&_ol_ul]:ml-10 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_td]:min-w-[80px] [&_td]:border [&_td]:border-gray-300 [&_td]:p-2 [&_th]:min-w-[80px] [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:p-2 [&_th]:font-semibold [&_ul]:mb-3 [&_ul]:list-disc [&_ul_ol]:ml-10 [&_ul_ul]:ml-10"
                />
            </div>

            {/* Toolbar */}
            <div
                className={`flex flex-row flex-wrap justify-between px-1 pb-1 ${toolbarBgColor ? toolbarBgColor : ""}`}
            >
                <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-t-md p-0">
                    {features.bold && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            className={btn("bold")}
                        >
                            <b>B</b>
                        </button>
                    )}
                    {features.italic && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            className={btn("italic")}
                        >
                            <i>I</i>
                        </button>
                    )}
                    {features.underline && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            className={btn("underline")}
                        >
                            <u>U</u>
                        </button>
                    )}
                    {features.strike && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                            className={btn("strike")}
                        >
                            <s>S</s>
                        </button>
                    )}
                    {features.heading && (
                        <>
                            <button
                                type="button"
                                onClick={() => toggleHeading(1)}
                                className={btn("heading", { level: 1 })}
                            >
                                H1
                            </button>
                            <button
                                type="button"
                                onClick={() => toggleHeading(2)}
                                className={btn("heading", { level: 2 })}
                            >
                                H2
                            </button>
                        </>
                    )}
                    {features.blockquote && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                            className={btn("blockquote")}
                        >
                            ❝
                        </button>
                    )}
                    {features.bulletList && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            className={btn("bulletList")}
                        >
                            • List
                        </button>
                    )}
                    {features.orderedList && (
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            className={btn("orderedList")}
                        >
                            1. List
                        </button>
                    )}
                    {features.indent && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    const isInList =
                                        editor.isActive("bulletList") ||
                                        editor.isActive("orderedList");
                                    if (isInList) {
                                        editor.chain().focus().sinkListItem("listItem").run();
                                    }
                                }}
                                disabled={
                                    !editor.isActive("bulletList") &&
                                    !editor.isActive("orderedList")
                                }
                                className="rounded p-1 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                                title="Nest list item"
                            >
                                <ArrowRight className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const isInList =
                                        editor.isActive("bulletList") ||
                                        editor.isActive("orderedList");
                                    if (isInList) {
                                        editor.chain().focus().liftListItem("listItem").run();
                                    }
                                }}
                                disabled={
                                    !editor.isActive("bulletList") &&
                                    !editor.isActive("orderedList")
                                }
                                className="rounded p-1 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                                title="Outdent list item"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                        </>
                    )}
                    {features.link && (
                        <button
                            type="button"
                            onClick={openLinkDialog}
                            className="rounded px-2 py-1 text-blue-500 hover:bg-gray-300"
                        >
                            <LinkIcon className="h-5 w-5" />
                        </button>
                    )}
                    {features.table && (
                        <button
                            type="button"
                            onClick={() =>
                                editor
                                    .chain()
                                    .focus()
                                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                                    .run()
                            }
                            className={btn("table")}
                            title="Insert table"
                        >
                            <TableIcon className="h-5 w-5" />
                        </button>
                    )}
                    {features.redoUndo && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    if (editor.can().undo()) {
                                        editor.commands.undo();
                                    }
                                }}
                                disabled={!editor.can().undo()}
                                className={`rounded px-2 py-1 ${
                                    !editor.can().undo()
                                        ? "cursor-not-allowed opacity-40"
                                        : "hover:bg-gray-300"
                                }`}
                            >
                                <Undo className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (editor.can().redo()) {
                                        editor.commands.redo();
                                    }
                                }}
                                disabled={!editor.can().redo()}
                                className={`rounded px-2 py-1 ${
                                    !editor.can().redo()
                                        ? "cursor-not-allowed opacity-40"
                                        : "hover:bg-gray-300"
                                }`}
                            >
                                <Redo className="h-5 w-5" />
                            </button>
                        </>
                    )}
                    {props.toolbarExtras}
                </div>

                {/* Table context toolbar */}
                {features.table && editor.isActive("table") && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 border-t border-gray-200 pt-1">
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().addColumnBefore().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs hover:bg-gray-300"
                            title="Add column before"
                        >
                            <Plus className="h-3 w-3" />
                            <Columns className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs hover:bg-gray-300"
                            title="Add column after"
                        >
                            <Columns className="h-3 w-3" />
                            <Plus className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().addRowBefore().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs hover:bg-gray-300"
                            title="Add row before"
                        >
                            <Plus className="h-3 w-3" />
                            <Rows className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().addRowAfter().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs hover:bg-gray-300"
                            title="Add row after"
                        >
                            <Rows className="h-3 w-3" />
                            <Plus className="h-3 w-3" />
                        </button>
                        <div className="mx-1 h-4 w-px bg-gray-300" />
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().deleteColumn().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
                            title="Delete column"
                        >
                            <Columns className="h-3 w-3" />
                            <Minus className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().deleteRow().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
                            title="Delete row"
                        >
                            <Rows className="h-3 w-3" />
                            <Minus className="h-3 w-3" />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().deleteTable().run()}
                            className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50"
                            title="Delete table"
                        >
                            <Trash2 className="h-3 w-3" />
                        </button>
                    </div>
                )}
            </div>

            <TipTapLinkDialog
                isOpen={isLinkDialogOpen}
                onClose={() => setLinkDialogOpen(false)}
                onSave={insertLink}
                title={title}
                url={url}
                setTitle={setTitle}
                setUrl={setUrl}
            />
        </div>
    );
});
